// Angular imports
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { DynamicDashboardService } from '../_services/dynamic-dashboard.service';

// Constant imports
import { ActionConstants } from '../../_constants/action-constants';
import { StringFormatConstants } from '../../_constants/string-format-constants';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { SharedWorkspaceParametersDto } from '../_model/shared-workspace-parameters-dto';
import { SharedWorkspaceResultDto } from '../_model/shared-workspace-result-dto';

interface IActionItem {
    action: string;
    text: string;
    icon: string;
    type: string;
    class: string;
}

@Component({
    selector: 'app-shared-workspaces',
    templateUrl: './shared-workspaces.component.html',
    styleUrls: ['./shared-workspaces.component.scss']
})
export class SharedWorkspacesComponent implements OnInit {
    @Input() visible = false;
    @Output() onClose = new EventEmitter<boolean>();
    @Output() onImport = new EventEmitter<SharedWorkspaceResultDto>();
    @Output() onEdit = new EventEmitter<SharedWorkspaceResultDto>();
    @Output() onDelete = new EventEmitter<SharedWorkspaceResultDto>();

    loadingVisible = false;
    dateFormat = StringFormatConstants.dateFormat;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconClose = IconConstants.CLOSE;

    actionItems: IActionItem[];
    selectedContext: PropertyCommunityInfoResultDto;
    sharedWorkspaceResults: SharedWorkspaceResultDto[];

    constructor(
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService,
        private readonly dashboardService: DynamicDashboardService
    ) {}

    ngOnInit(): void {
        this.actionItems = [
            { action: ActionConstants.IMPORT, text: 'Import', icon: IconConstants.COPY, type: 'default' },
            { action: ActionConstants.EDIT, text: 'Edit', icon: IconConstants.EDIT, type: 'default' },
            { action: ActionConstants.DELETE, text: 'Delete', icon: IconConstants.DELETE, type: 'danger' }
        ].map((action) => {
            return {
                action: action.action,
                text: action.text,
                icon: action.icon,
                type: action.type,
                class: 'icon-button-content'
            };
        });

        this.selectedContext = this.dashboardService.getLocalContextInfo() || { communityId: null };
        this.dashboardService.getLocalContext().subscribe(() => {
            this.selectedContext = this.dashboardService.getLocalContextInfo() || { communityId: null };
        });
        this.populateSharedWorkspaces(true);
    }

    onShown(e: any): void {
        e.component.repaint();
    }

    onHidden(e: any): void {
        this.visible = false;
        this.sharedWorkspaceResults = null;
        this.onClose.emit(false);
    }

    hasVisibleAction(rec: SharedWorkspaceResultDto): boolean {
        let hasAction = false;
        for (const item of this.actionItems) {
            hasAction = hasAction || this.isActionVisible(item, rec);
        }
        return hasAction;
    }

    isActionVisible(item: IActionItem, rec: SharedWorkspaceResultDto): boolean {
        let isVisible = false;
        const isOwner = rec.createdBy.id === this.userService.getCurrentUserId();
        switch (item.action) {
            case ActionConstants.IMPORT:
                isVisible = !isOwner && !rec.allowEdit;
                break;
            case ActionConstants.EDIT:
                isVisible = rec.allowEdit;
                break;
            case ActionConstants.DELETE:
                isVisible = rec.allowDelete;
                break;
        }
        return isVisible;
    }

    onActionClick(item: IActionItem, rec: SharedWorkspaceResultDto): void {
        switch (item.action) {
            case ActionConstants.IMPORT:
                if (this.userService.isITUser()) {
                    this.onEdit.emit(rec);
                } else {
                    this.onImport.emit(rec);
                }
                break;
            case ActionConstants.EDIT:
                this.onEdit.emit(rec);
                break;
            case ActionConstants.DELETE:
                this.onDelete.emit(rec);
                break;
        }
    }

    private populateSharedWorkspaces(clearCache = false): void {
        this.loadingVisible = true;
        const params = new SharedWorkspaceParametersDto();
        params.isSystemType = false;
        params.clearCache = clearCache;
        params.categoryCode = this.dashboardService.getWorkspaceCategoryCode();
        this.dashboardService.getSharedWorkspaces(params).subscribe(
            (results) => {
                const userDashboards = this.dashboardService.getUserDashboards();
                const userSharedWorkspaces = userDashboards
                    .filter((r) => !!r.sharedWorkspace)
                    .map((r) => r.sharedWorkspace.key);
                this.sharedWorkspaceResults = results.filter((r) => {
                    let isValid = r.allowEdit || r.allowDelete;
                    if (!isValid) {
                        isValid = userSharedWorkspaces.indexOf(r.key) === -1;
                    }
                    return isValid;
                });
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
